import React from 'react';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { red } from '@mui/material/colors';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { green } from '@mui/material/colors';
import DeleteIcon from '@mui/icons-material/Delete';
import locationDefault from '../../../../images/locationdefault.jpg';
import { ButtonBase, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PreviewAsset from '../../../Common/PreviewAsset';
import { CampaignAdvertisement } from '@horasys/bellplus/common/types';
import { getPrimaryAssetType } from 'services/advertising.service';
import { CampaignState } from 'Stores/Slices/campaignSlice';
import { useAppSelector } from 'Stores/Hooks/hooks';
import { RootState } from 'Stores/store';

const PREFIX = 'AdvertisementCardGrid';

const classes = {
	root: `${PREFIX}-root`,
	media: `${PREFIX}-media`,
	expand: `${PREFIX}-expand`,
	expandOpen: `${PREFIX}-expandOpen`,
	avatar: `${PREFIX}-avatar`,
	fabGreen: `${PREFIX}-fabGreen`,
	fabRed: `${PREFIX}-fabRed`,
	manualCalculate: `${PREFIX}-manualCalculate`,
	buttonGroup: `${PREFIX}-buttonGroup`,
	paddingSelectedDevices: `${PREFIX}-paddingSelectedDevices`,
	dummyCard: `${PREFIX}-dummyCard`,
	buttonBase: `${PREFIX}-buttonBase`,
	adCard: `${PREFIX}-adCard`,
};
const Root = styled('div')(({ theme }) => ({
	[`& .${classes.root}`]: {
		maxWidth: 345,
	},

	[`& .${classes.media}`]: {
		height: 150,
		paddingTop: '56.25%', // 16:9
	},

	[`& .${classes.expand}`]: {
		transform: 'rotate(0deg)',
		marginLeft: 'auto',
		transition: theme.transitions.create('transform', {
			duration: theme.transitions.duration.shortest,
		}),
	},

	[`& .${classes.expandOpen}`]: {
		transform: 'rotate(180deg)',
	},

	[`& .${classes.avatar}`]: {
		backgroundColor: red[500],
	},

	[`& .${classes.fabGreen}`]: {
		marginTop: theme.spacing(1),
		color: theme.palette.common.white,
		backgroundColor: green[500],
		'&:hover': {
			backgroundColor: green[600],
		},
	},

	[`& .${classes.fabRed}`]: {
		marginTop: theme.spacing(1),
		color: theme.palette.common.white,
		backgroundColor: red[500],
		'&:hover': {
			backgroundColor: red[600],
		},
	},

	[`& .${classes.manualCalculate}`]: {
		marginTop: theme.spacing(3),
	},

	[`& .${classes.buttonGroup}`]: {
		marginTop: theme.spacing(1),
	},

	[`& .${classes.paddingSelectedDevices}`]: {
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(1),
		paddingTop: theme.spacing(1),
	},

	[`& .${classes.dummyCard}`]: {
		padding: theme.spacing(2),
		minHeight: 400,
		backgroundColor: '#ececec',
		width: '1100px',
	},

	[`& .${classes.buttonBase}`]: {
		minWidth: '100%',
		minHeight: '100%',
		backgroundColor: '#ececec',
	},

	[`& .${classes.adCard}`]: {
		padding: theme.spacing(2),
	},
}));

/**
 * Displaying for location name and its devices. used in manual mode of slot allocation.
 * @param {*} param0
 */

interface Props {
	handleAddAdvertisement?: any;
	handleEditAdvertisement?: any;
	handleViewAdvertisement?: any;
	handleDeleteAdvertisement?: any;
}
export default function AdvertisementCardGrid({
	handleAddAdvertisement,
	handleEditAdvertisement,
	handleViewAdvertisement,
	handleDeleteAdvertisement,
}: Props) {
	const campaignStore: CampaignState = useAppSelector(
		(state: RootState) => state.campaign,
	);
	const { campaign, mode } = campaignStore;

	const getDevicesCount = (locations: any) => {
		let devicesCount = 0;
		locations &&
			locations.forEach((loc: any) => {
				devicesCount = devicesCount + loc.selectedDeviceCount;
			});
		return devicesCount;
	};

	const AdCard = ({ data, index }: { data: CampaignAdvertisement; index: any }) => (
		<Grid xs={3} className={classes.adCard}>
			<Card className={classes.root}>
				<CardHeader
					avatar={
						<Avatar aria-label="location" className={classes.avatar}>
							{data?.advertisement?.name?.charAt(0)}
						</Avatar>
					}
					title={
						data.previewUrl ? (
							<PreviewAsset
								srcpath={data.previewUrl}
								type={
									getPrimaryAssetType(data.advertisement)
										? getPrimaryAssetType(data.advertisement)
										: 'video'
								}
								filename={data.advertisement.name}
								buttontype="link"
							/>
						) : (
							data?.advertisement?.name
						)
					}
					subheader="Advertisement"
				/>
				<CardMedia
					className={classes.media}
					image={data.thumbnailUrl ? data.thumbnailUrl : locationDefault}
					title={data.advertisement.name}
				/>
				<CardContent>
					<Typography variant="subtitle2" component="h6">
						Start Date: {moment(data.startDate).format('YYYY-MM-DD')}
					</Typography>
					<Typography variant="subtitle2" component="h6">
						End Date: {moment(data.endDate).format('YYYY-MM-DD')}
					</Typography>

					<Typography variant="subtitle2" component="h6">
						Locations :{' '}
						{data.typeOfSearch === 'location'
							? data.selectedLocations && data.selectedLocations.length
							: data.selectedLocationGroups &&
							  data.selectedLocationGroups.length}
					</Typography>

					<Typography variant="subtitle2" component="h6">
						Budget : {data.calculatedBudget}
					</Typography>

					<Typography variant="subtitle2" component="h6">
						Selected Devices :{' '}
						{data.typeOfSearch === 'location'
							? getDevicesCount(data.selectedLocations)
							: getDevicesCount(data.selectedLocationGroups)}
					</Typography>
				</CardContent>
				<CardActions disableSpacing>
					{mode !== 'EDIT' && mode !== 'VIEW' ? (
						<Root>
							<IconButton
								aria-label="View"
								onClick={() => handleViewAdvertisement(index)}
								size="large">
								<VisibilityIcon />
							</IconButton>
							{
								<>
									<IconButton
										aria-label="Edit"
										onClick={() => handleEditAdvertisement(index)}
										size="large">
										<EditIcon />
									</IconButton>

									<IconButton
										aria-label="Delete"
										onClick={() => handleDeleteAdvertisement(index)}
										size="large">
										<DeleteIcon />
									</IconButton>
								</>
							}
						</Root>
					) : null}
				</CardActions>
			</Card>
		</Grid>
	);

	return (
		<>
			<Root>
				<Grid container xs={12}>
					{mode === 'CREATE' && (
						<Grid
							container
							xs={3}
							className={classes.dummyCard}
							justifyContent="center"
							alignItems="center">
							<ButtonBase
								className={classes.buttonBase}
								onClick={handleAddAdvertisement}>
								<Tooltip
									title="Add Advertisement"
									placement="right-start">
									<AddCircleOutlineIcon
										color="primary"
										style={{ fontSize: 60, color: '#042630' }}
									/>
								</Tooltip>
							</ButtonBase>
						</Grid>
					)}

					{campaign.campaignAdvertisements.map((det, index) => (
						<AdCard key={index} data={det} index={index} />
					))}
				</Grid>
			</Root>
		</>
	);
}
